import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useChat } from "@/context/ChatContext";
import { formatTime } from "@/lib/chat";
import { formatPrice } from "@/lib/format";
import { X, Send, Loader2, Check, CheckCheck, MessageCircle, ImagePlus, HandCoins, BadgeCheck, Ban, ChevronLeft, Sparkles, CalendarClock } from "lucide-react";
import { ChatConversationList } from "@/components/ChatConversationList";
import { toast } from "sonner";

const AI_LISTING_SENDER_ID = "__ai_listing_assistant__";

function ViewingProposalBubble({ msg, isSeller, onSellerRespond, onBuyerRespond, responding }) {
  const vp = msg.viewing_proposal;
  const [altOpen, setAltOpen] = useState(false);
  const [altTime, setAltTime] = useState("");
  if (!vp) return null;
  const status = vp.status || "pending_seller";
  const label = vp.summary || vp.proposed_time || "Javasolt megtekintés";

  const submitAlt = (e) => {
    e.preventDefault();
    const t = altTime.trim();
    if (t.length < 3) {
      toast.error("Írd le az új időpontot (pl. szombat 14:00).");
      return;
    }
    onSellerRespond(vp.id, "reschedule", t);
    setAltOpen(false);
    setAltTime("");
  };

  return (
    <div
      data-testid={`viewing-proposal-${msg.id}`}
      className="mt-2 rounded-xl border border-violet-400/30 bg-violet-500/10 px-3 py-2.5 text-xs"
    >
      <div className="flex items-center gap-1.5 text-violet-200/90 uppercase tracking-wide text-[10px]">
        <CalendarClock className="w-3.5 h-3.5" /> Megtekintés
      </div>
      <div className="text-sm text-white mt-1">{label}</div>
      {status === "pending_seller" && isSeller && (
        <>
          <div className="flex flex-wrap gap-2 mt-2.5">
            <button
              type="button"
              data-testid={`viewing-confirm-${vp.id}`}
              disabled={responding}
              onClick={() => onSellerRespond(vp.id, "confirm")}
              className="flex-1 min-w-[88px] py-1.5 px-2 rounded-full bg-emerald-500/90 hover:bg-emerald-500 text-white text-[11px] font-medium disabled:opacity-50"
            >
              Megerősítem
            </button>
            <button
              type="button"
              data-testid={`viewing-alt-toggle-${vp.id}`}
              disabled={responding}
              onClick={() => setAltOpen((v) => !v)}
              className="flex-1 min-w-[88px] py-1.5 px-2 rounded-full bg-amber-500/20 hover:bg-amber-500/30 text-amber-100 text-[11px] font-medium disabled:opacity-50"
            >
              Más időpont
            </button>
            <button
              type="button"
              data-testid={`viewing-decline-${vp.id}`}
              disabled={responding}
              onClick={() => onSellerRespond(vp.id, "decline")}
              className="flex-1 min-w-[88px] py-1.5 px-2 rounded-full bg-red-500/15 hover:bg-red-500/25 text-red-300 text-[11px] font-medium disabled:opacity-50"
            >
              Nem jó
            </button>
          </div>
          {altOpen && (
            <form onSubmit={submitAlt} className="mt-2.5 space-y-2" data-testid={`viewing-alt-form-${vp.id}`}>
              <input
                type="text"
                data-testid={`viewing-alt-input-${vp.id}`}
                value={altTime}
                onChange={(e) => setAltTime(e.target.value)}
                placeholder="Pl. vasárnap 11:00, vagy hétfő 17:30"
                className="w-full rounded-lg bg-black/25 border border-white/15 px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:border-amber-400/50 outline-none"
                autoFocus
              />
              <button
                type="submit"
                data-testid={`viewing-alt-send-${vp.id}`}
                disabled={responding}
                className="w-full py-1.5 rounded-full bg-amber-500/90 hover:bg-amber-500 text-white text-[11px] font-medium disabled:opacity-50"
              >
                Ellentjavaslat küldése
              </button>
            </form>
          )}
        </>
      )}
      {status === "pending_buyer" && !isSeller && (
        <div className="flex flex-wrap gap-2 mt-2.5">
          <button
            type="button"
            data-testid={`viewing-buyer-accept-${vp.id}`}
            disabled={responding}
            onClick={() => onBuyerRespond(vp.id, "confirm")}
            className="flex-1 min-w-[88px] py-1.5 px-2 rounded-full bg-emerald-500/90 hover:bg-emerald-500 text-white text-[11px] font-medium disabled:opacity-50"
          >
            Elfogadom
          </button>
          <button
            type="button"
            data-testid={`viewing-buyer-decline-${vp.id}`}
            disabled={responding}
            onClick={() => onBuyerRespond(vp.id, "decline")}
            className="flex-1 min-w-[88px] py-1.5 px-2 rounded-full bg-red-500/15 hover:bg-red-500/25 text-red-300 text-[11px] font-medium disabled:opacity-50"
          >
            Nem jó most
          </button>
        </div>
      )}
      {status === "pending_buyer" && isSeller && (
        <div className="text-amber-200/90 mt-1.5 text-[11px]">Ellenjavaslat elküldve — a vevő válaszára vársz.</div>
      )}
      {status === "confirmed" && (
        <div className="text-emerald-400 mt-1.5 text-[11px]">Megerősítve</div>
      )}
      {status === "declined" && (
        <div className="text-red-300/90 mt-1.5 text-[11px]">Elutasítva — egyeztessetek új időpontot</div>
      )}
      {status === "declined_by_buyer" && (
        <div className="text-red-300/90 mt-1.5 text-[11px]">A vevő nem tudta az ellenjavaslatot — egyeztessetek tovább</div>
      )}
    </div>
  );
}

function OfferBubble({ msg, mine, onRespond, responding }) {
  const status = msg.offer_status;
  return (
    <div data-testid={`offer-bubble-${msg.id}`} className={`rounded-2xl px-4 py-3 border ${mine ? "bg-electric/15 border-electric/40" : "bg-white/[0.06] border-white/15"}`}>
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-slate-400">
        <HandCoins className="w-3.5 h-3.5 text-electric" strokeWidth={1.5} /> Vételi ajánlat
      </div>
      <div className="font-heading text-lg font-medium mt-1 text-white">{formatPrice(msg.offer_amount)}</div>
      {status === "pending" && !mine && (
        <div className="flex gap-2 mt-2.5">
          <button
            data-testid={`offer-accept-${msg.id}`}
            disabled={responding}
            onClick={() => onRespond(msg, "accept")}
            className="flex-1 inline-flex items-center justify-center gap-1.5 py-1.5 px-3 rounded-full bg-emerald-500/90 hover:bg-emerald-500 text-white text-xs font-medium transition-all disabled:opacity-50"
          >
            <BadgeCheck className="w-3.5 h-3.5" /> Elfogad
          </button>
          <button
            data-testid={`offer-reject-${msg.id}`}
            disabled={responding}
            onClick={() => onRespond(msg, "reject")}
            className="flex-1 inline-flex items-center justify-center gap-1.5 py-1.5 px-3 rounded-full bg-red-500/15 hover:bg-red-500/25 text-red-400 text-xs font-medium transition-all disabled:opacity-50"
          >
            <Ban className="w-3.5 h-3.5" /> Elutasít
          </button>
        </div>
      )}
      {status === "pending" && mine && (
        <div className="text-xs text-slate-400 mt-1.5">Válaszra vár…</div>
      )}
      {status === "accepted" && (
        <div data-testid={`offer-status-${msg.id}`} className="inline-flex items-center gap-1.5 mt-2 px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-400 text-xs font-medium">
          <BadgeCheck className="w-3.5 h-3.5" /> Elfogadva
        </div>
      )}
      {status === "rejected" && (
        <div data-testid={`offer-status-${msg.id}`} className="inline-flex items-center gap-1.5 mt-2 px-2.5 py-1 rounded-full bg-red-500/15 text-red-400 text-xs font-medium">
          <Ban className="w-3.5 h-3.5" /> Elutasítva
        </div>
      )}
    </div>
  );
}

function ChatWindow({ conv, onClose, onBack }) {
  const { user } = useAuth();
  const { lastMessage, typingEvent, sendTyping, wsLive } = useChat();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [offerOpen, setOfferOpen] = useState(false);
  const [offerAmount, setOfferAmount] = useState("");
  const [responding, setResponding] = useState(false);
  const [otherTyping, setOtherTyping] = useState(false);
  const endRef = useRef();
  const fileRef = useRef();
  const lastTypingSent = useRef(0);
  const typingTimer = useRef(null);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const r = await api.get(`/conversations/${conv.id}/messages`);
      setMessages(r.data.messages);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => { load(); }, [conv.id]);

  useEffect(() => {
    if (wsLive) return undefined;
    const id = window.setInterval(() => load(true), 2000);
    return () => window.clearInterval(id);
  }, [conv.id, wsLive]);

  useEffect(() => {
    if (!lastMessage || lastMessage.conversation_id !== conv.id) return;
    const m = lastMessage.message;
    if (lastMessage.type === "offer_update") {
      setMessages((list) => list.map((x) => (x.id === m.id ? { ...x, ...m } : x)));
      return;
    }
    setMessages((list) => (list.some((x) => x.id === m.id) ? list : [...list, m]));
    if (m.sender_id !== user.id) {
      setOtherTyping(false);
      api.get(`/conversations/${conv.id}/messages`).catch(() => {});
    }
  }, [lastMessage]);

  useEffect(() => {
    if (typingEvent && typingEvent.conversation_id === conv.id && typingEvent.user_id !== user.id) {
      setOtherTyping(true);
      clearTimeout(typingTimer.current);
      typingTimer.current = setTimeout(() => setOtherTyping(false), 3000);
    }
    return () => clearTimeout(typingTimer.current);
  }, [typingEvent]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, otherTyping]);

  const onInputChange = (e) => {
    setText(e.target.value);
    const now = Date.now();
    if (now - lastTypingSent.current > 1500) {
      lastTypingSent.current = now;
      sendTyping(conv.id);
    }
  };

  const appendSent = (sent) => {
    setMessages((m) => (m.some((x) => x.id === sent.id) ? m : [...m, sent]));
  };

  const send = async (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t) return;
    setText("");
    setSending(true);
    try {
      const r = await api.post(`/conversations/${conv.id}/messages`, { text: t });
      appendSent(r.data);
      if (!wsLive) window.setTimeout(() => load(true), 1500);
    } catch (err) {
      setText(t);
    } finally {
      setSending(false);
    }
  };

  const sendImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("files", file);
      const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = up.data.urls?.[0];
      if (!url) throw new Error("no url");
      const r = await api.post(`/conversations/${conv.id}/messages`, { text: "", image_url: url });
      appendSent(r.data);
    } catch (err) {
      toast.error("A kép küldése sikertelen");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const sendOffer = async (e) => {
    e.preventDefault();
    const amount = parseInt(offerAmount, 10);
    if (!amount || amount <= 0) { toast.error("Adj meg érvényes összeget"); return; }
    setSending(true);
    try {
      const r = await api.post(`/conversations/${conv.id}/messages`, { text: "", offer_amount: amount });
      appendSent(r.data);
      setOfferOpen(false);
      setOfferAmount("");
    } catch (err) {
      toast.error("Az ajánlat küldése sikertelen");
    } finally {
      setSending(false);
    }
  };

  const respondOffer = async (msg, action) => {
    setResponding(true);
    try {
      const r = await api.post(`/conversations/${conv.id}/offers/${msg.id}/respond`, { action });
      setMessages((list) => list.map((x) => (x.id === msg.id ? { ...x, ...r.data } : x)));
    } catch (err) {
      toast.error(err.response?.data?.detail || "A válasz sikertelen");
    } finally {
      setResponding(false);
    }
  };

  const patchViewingInMessages = (list, viewingId, viewing) => {
    const st = viewing?.status;
    const summary = viewing?.summary;
    const proposed = viewing?.proposed_time;
    return list.map((x) => {
      if (x.viewing_proposal?.id !== viewingId) return x;
      return {
        ...x,
        viewing_proposal: {
          ...x.viewing_proposal,
          status: st || x.viewing_proposal.status,
          ...(summary ? { summary } : {}),
          ...(proposed ? { proposed_time: proposed } : {}),
        },
      };
    });
  };

  const respondViewingSeller = async (viewingId, action, newTime) => {
    setResponding(true);
    try {
      const body = { action };
      if (action === "reschedule") body.new_time = newTime;
      const r = await api.post(`/my/listing-viewings/${viewingId}/respond`, body);
      const sys = r.data.system_message;
      setMessages((list) => patchViewingInMessages(list, viewingId, r.data.viewing).concat(sys ? [sys] : []));
      const ok =
        action === "confirm"
          ? "Megtekintés megerősítve"
          : action === "reschedule"
            ? "Ellenjavaslat elküldve"
            : "Válasz elküldve";
      toast.success(ok);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Az időpont kezelése sikertelen");
    } finally {
      setResponding(false);
    }
  };

  const respondViewingBuyer = async (viewingId, action) => {
    setResponding(true);
    try {
      const r = await api.post(`/listing-viewings/${viewingId}/buyer-respond`, { action });
      const sys = r.data.system_message;
      setMessages((list) => patchViewingInMessages(list, viewingId, r.data.viewing).concat(sys ? [sys] : []));
      toast.success(action === "confirm" ? "Időpont elfogadva" : "Válasz elküldve");
    } catch (err) {
      toast.error(err.response?.data?.detail || "A válasz sikertelen");
    } finally {
      setResponding(false);
    }
  };

  const myLastReadIdx = (() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].sender_id === user.id) return messages[i].read ? i : -1;
    }
    return -1;
  })();

  return (
    <div
      data-testid="chat-window"
      className="fixed bottom-0 right-0 sm:bottom-6 sm:right-6 z-[60] w-full sm:w-[380px] h-[70vh] sm:h-[520px] max-h-[calc(100vh-1rem)] flex flex-col glass-strong sm:rounded-3xl border border-white/10 overflow-hidden shadow-2xl"
    >
      {/* Header */}
      <div className="flex items-center gap-2 p-4 border-b border-white/10 bg-anthracite-800">
        {onBack && (
          <button
            type="button"
            data-testid="chat-back-inbox"
            onClick={onBack}
            aria-label="Vissza az üzenetekhez"
            className="p-1.5 rounded-full hover:bg-white/10 shrink-0"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
        )}
        <div className="w-10 h-10 rounded-xl overflow-hidden bg-white/5 shrink-0">
          {conv.car_image ? <img src={conv.car_image} alt="" className="w-full h-full object-cover" /> : <MessageCircle className="w-5 h-5 text-electric m-2.5" />}
        </div>
        <div className="flex-1 min-w-0">
          {conv.other_seller_public?.slug && !conv.is_seller ? (
            <Link
              to={`/kereskedes/${conv.other_seller_public.slug}`}
              data-testid="chat-dealer-profile-link"
              className="font-heading text-sm font-medium truncate text-electric hover:underline block"
            >
              {conv.other_seller_public.display_name || conv.other_name}
            </Link>
          ) : (
            <div className="font-heading text-sm font-medium truncate">{conv.other_name}</div>
          )}
          <div className="text-xs text-slate-400 truncate">
            {otherTyping ? <span data-testid="typing-indicator" className="text-electric animate-pulse">gépel…</span> : conv.car_label}
          </div>
        </div>
        <button data-testid="chat-close" onClick={onClose} className="p-1.5 rounded-full hover:bg-white/10">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 text-electric animate-spin" /></div>
        ) : messages.length === 0 ? (
          <div className="text-center text-slate-400 text-sm py-10">Írd meg az első üzeneted az eladónak.</div>
        ) : (
          messages.map((m, i) => {
            const mine = m.sender_id === user.id;
            const isAi = m.kind === "ai_assistant" || m.sender_id === AI_LISTING_SENDER_ID;
            if (m.kind === "system") {
              return (
                <div key={m.id} className="flex justify-center" data-testid={`system-msg-${m.id}`}>
                  <div className="max-w-[90%] text-center text-[11px] text-slate-400 bg-white/[0.04] border border-white/10 rounded-full px-3.5 py-1.5">
                    {m.text}
                  </div>
                </div>
              );
            }
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className="max-w-[78%]">
                  {m.kind === "offer" ? (
                    <OfferBubble msg={m} mine={mine} onRespond={respondOffer} responding={responding} />
                  ) : (
                    <div
                      className={`rounded-2xl px-3.5 py-2 ${
                        isAi
                          ? "bg-violet-500/15 border border-violet-400/25 text-slate-100 rounded-bl-sm"
                          : mine
                            ? "bg-electric text-white rounded-br-sm"
                            : "bg-white/8 text-slate-100 rounded-bl-sm"
                      }`}
                    >
                      {isAi && (
                        <div className="mb-1.5">
                          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-violet-300/90">
                            <Sparkles className="w-3 h-3" /> Gyors válasz
                          </div>
                          <div className="text-[10px] text-slate-500 mt-0.5 leading-snug">
                            Az eladó briefje alapján — személyesen is válaszolhat.
                          </div>
                        </div>
                      )}
                      {m.image_url && (
                        <a href={m.image_url} target="_blank" rel="noreferrer" data-testid={`chat-image-${m.id}`}>
                          <img src={m.image_url} alt="küldött kép" className="rounded-xl max-h-44 w-full object-cover my-1" />
                        </a>
                      )}
                      {m.text && <div className="text-sm leading-snug break-words">{m.text}</div>}
                      {m.viewing_proposal && (
                        <ViewingProposalBubble
                          msg={m}
                          isSeller={conv.is_seller}
                          onSellerRespond={respondViewingSeller}
                          onBuyerRespond={respondViewingBuyer}
                          responding={responding}
                        />
                      )}
                    </div>
                  )}
                  <div className={`flex items-center gap-1 mt-1 text-[10px] ${mine ? "text-white/70 justify-end" : "text-slate-400"}`}>
                    {formatTime(m.created_at)}
                    {mine && !isAi && (i === myLastReadIdx ? <CheckCheck className="w-3 h-3" /> : <Check className="w-3 h-3" />)}
                  </div>
                </div>
              </div>
            );
          })
        )}
        {otherTyping && (
          <div className="flex justify-start" data-testid="typing-bubble">
            <div className="rounded-2xl rounded-bl-sm px-3.5 py-2.5 bg-white/8">
              <span className="inline-flex gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "0ms" }} />
                <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "150ms" }} />
                <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "300ms" }} />
              </span>
            </div>
          </div>
        )}
        {myLastReadIdx >= 0 && myLastReadIdx === messages.length - 1 && (
          <div className="text-right text-[10px] text-slate-500 pr-1" data-testid="read-indicator">Olvasva</div>
        )}
        <div ref={endRef} />
      </div>

      {/* Offer panel */}
      {offerOpen && (
        <form onSubmit={sendOffer} data-testid="offer-panel" className="px-3 py-2.5 border-t border-white/10 bg-anthracite-800 flex items-center gap-2">
          <input
            data-testid="offer-amount-input"
            type="number"
            min="1"
            value={offerAmount}
            onChange={(e) => setOfferAmount(e.target.value)}
            placeholder="Ajánlatod összege (Ft)"
            className="flex-1 bg-white/[0.04] border border-white/10 rounded-full px-4 py-2 text-sm text-white placeholder:text-slate-500 focus:border-electric outline-none"
            autoFocus
          />
          <button
            data-testid="offer-send"
            type="submit"
            disabled={sending}
            className="px-4 py-2 rounded-full bg-electric hover:bg-electric-light text-white text-xs font-medium transition-all disabled:opacity-50"
          >
            Küldés
          </button>
          <button type="button" data-testid="offer-cancel" onClick={() => setOfferOpen(false)} className="p-1.5 rounded-full hover:bg-white/10">
            <X className="w-3.5 h-3.5" />
          </button>
        </form>
      )}

      {/* Input */}
      <form onSubmit={send} className="p-3 border-t border-white/10 flex items-center gap-1.5 bg-anthracite-800">
        <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="chat-image-input"
          onChange={(e) => sendImage(e.target.files?.[0])} />
        <button
          data-testid="chat-image-btn"
          type="button"
          disabled={uploading}
          onClick={() => fileRef.current?.click()}
          title="Kép küldése"
          className="w-9 h-9 shrink-0 rounded-full glass hover:bg-white/10 flex items-center justify-center transition-all disabled:opacity-50"
        >
          {uploading ? <Loader2 className="w-4 h-4 animate-spin text-electric" /> : <ImagePlus className="w-4 h-4 text-slate-300" strokeWidth={1.5} />}
        </button>
        <button
          data-testid="chat-offer-btn"
          type="button"
          onClick={() => setOfferOpen((o) => !o)}
          title="Ajánlatot teszek"
          className={`w-9 h-9 shrink-0 rounded-full flex items-center justify-center transition-all ${offerOpen ? "bg-electric text-white" : "glass hover:bg-white/10 text-slate-300"}`}
        >
          <HandCoins className="w-4 h-4" strokeWidth={1.5} />
        </button>
        <input
          data-testid="chat-input"
          value={text}
          onChange={onInputChange}
          placeholder="Írj üzenetet…"
          className="flex-1 min-w-0 bg-white/[0.04] border border-white/10 rounded-full px-4 py-2.5 text-sm text-white placeholder:text-slate-500 focus:border-electric outline-none"
        />
        <button
          data-testid="chat-send"
          type="submit"
          disabled={sending || !text.trim()}
          className="w-10 h-10 shrink-0 rounded-full bg-electric hover:bg-electric-light disabled:opacity-40 flex items-center justify-center transition-all"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </form>
    </div>
  );
}

function ChatInboxPanel({ convs, onSelect, onClose }) {
  return (
    <div
      data-testid="chat-inbox-panel"
      className="fixed bottom-0 right-0 sm:bottom-6 sm:right-6 z-[60] w-full sm:w-[380px] h-[70vh] sm:h-[520px] max-h-[calc(100vh-1rem)] flex flex-col glass-strong sm:rounded-3xl border border-white/10 overflow-hidden shadow-2xl"
    >
      <div className="flex items-center gap-3 p-4 border-b border-white/10 bg-anthracite-800">
        <div className="w-10 h-10 rounded-xl bg-electric/15 flex items-center justify-center shrink-0">
          <MessageCircle className="w-5 h-5 text-electric" strokeWidth={1.75} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading text-sm font-medium">Üzenetek</div>
          <div className="text-xs text-slate-400">Beszélgetések az eladókkal és vevőkkel</div>
        </div>
        <button data-testid="chat-inbox-close" type="button" onClick={onClose} className="p-1.5 rounded-full hover:bg-white/10">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto">
        <ChatConversationList
          convs={convs}
          onSelect={onSelect}
          testidPrefix="inbox-conversation"
        />
      </div>
    </div>
  );
}

export function ChatDock() {
  const { user } = useAuth();
  const { convs, inboxOpen, openConv, openChatConv, backToInbox, closePanel } = useChat();

  if (!user) return null;

  const panelOpen = inboxOpen || openConv;

  return (
    <>
      {panelOpen && !openConv && (
        <ChatInboxPanel convs={convs} onSelect={openChatConv} onClose={closePanel} />
      )}

      {openConv && (
        <ChatWindow conv={openConv} onClose={closePanel} onBack={backToInbox} />
      )}
    </>
  );
}
